import { Injectable, computed, inject, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { ClientStore } from '../stores/client.store';
import { TenantStore } from '../stores/tenant.store';

import type { DemoRequest, DemoResponse } from './demo-backend';

const SESSION_KEY = 'demo.session.active';

type DemoBackendModule = typeof import('./demo-backend');

function readSessionFlag(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    return;
  }
}

function clearSessionFlag(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    return;
  }
}

@Injectable({ providedIn: 'root' })
export class DemoModeService {
  private readonly tenantStore = inject(TenantStore);
  private readonly clientStore = inject(ClientStore);

  private readonly activeSignal = signal(false);
  private backend: DemoBackendModule | null = null;

  readonly active = this.activeSignal.asReadonly();

  readonly available = environment.demo.enabled || !environment.production;

  readonly autoStart = this.available && environment.demo.autoStart;

  readonly exitLabel = computed(() => (this.autoStart ? 'Restart demo' : 'Exit demo'));

  async bootstrap(): Promise<boolean> {
    if (!this.available) {
      return false;
    }
    if (!this.autoStart && !readSessionFlag()) {
      return false;
    }
    await this.enter();
    return true;
  }

  async enter(): Promise<void> {
    if (!this.available) {
      return;
    }

    this.backend = await import('./demo-backend');
    this.activeSignal.set(true);
    writeSessionFlag();

    await this.tenantStore.init();
    await this.clientStore.init();
  }

  resolve(request: DemoRequest): DemoResponse | null {
    return this.backend?.resolveDemoRequest(request) ?? null;
  }

  async exit(): Promise<void> {
    this.backend?.resetDemoSessionState();
    clearSessionFlag();
    await this.clientStore.clear();
    await this.tenantStore.clear();
    this.activeSignal.set(false);
    window.location.replace('/');
  }
}
